import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {useState} from 'react';
import {useMemo} from 'react';
import {EventWizardDetails} from '@Components/event-wizard/event-wizard-details';
import {EventNav} from '@Components/event-wizard/event-nav';
import {setReduxForEventPresetPrefill} from '@Components/event-wizard/event-wizard-preset-prefill.helper';
import {EventFooter} from '@Components/event-wizard/event-footer';
import {Preview} from '@Components/event-wizard/preview';
import {EventWizardReview} from '@Components/event-wizard/event-wizard-review';
import {EventWizardTicketing} from '@Components/event-wizard/event-wizard-ticketing';
import type {EventWizardContainerQuery, EventWizardProps} from '@Components/event-wizard/event-wizard.types';
import {EventWizardProgressStep, EventWizardSourceType} from '@Components/event-wizard/event-wizard.types';
import {useDispatch} from 'react-redux';
import {
  getDefaultEventTeamShareDetails,
  getDefaultOrganiserDetails,
  getDefaultRegistrationDetails,
  onEventWizardAllTypesChange,
  onEventWizardDoesPAYGUserHaveAITemplateLimitUpdate,
  onEventWizardGoogleMapAPIKeyChange,
  onEventWizardOrganiserUpdate,
  onEventWizardIsEditingPublishedEventUpdate,
  onEventWizardPageSourceTypeUpdate,
  onEventWizardRegistrationChange,
  onEventWizardShareInTeamToggle,
  onEventWizardShouldUpsellRecurring,
  onEventWizardTeamNameUpdate,
  onEventWizardTicketingGatingPropsUpdate,
  onEventWizardUpdateShouldShowRecurringEventDetails,
  onEventWizardUserPastOfflineVenueChange,
} from '@Components/event-wizard/event-wizard-slice';
import {useEventWizardCurrentProgressStep, useEventWizardIsDraftBeingSaved, useEventWizardIsEventBeingCreated, useEventWizardWizardContainerQuery} from '@Components/event-wizard/event-wizard.hooks';
import {hasEventWizardUnsavedContent, markEventWizardContentAsSaved, setReduxForDesignPrefill, setReduxForStartDatePrefill, setReduxForUpdatingEvent} from '@Components/event-wizard/event-wizard.helper';
import {applyNewEventDefaultStyle} from '@Components/event-wizard/event-wizard-new-event-style.helper';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {CircularProgressLoader} from '@Components/circular-progress-loader';
import styles from './event-wizard.module.scss';

export function EventWizard({
  googleMapAPIKey,
  organisers,
  types,
  teamName,
  teamSize,
  customRegisterBtnTexts,
  userPastVenues,
  eventWizardSourceType,
  eventVO,
  eventVOMinifiedWizardPrefill,
  latestEventStyle,
  doesPAYGUserHaveAITemplateLimit,
  shouldUpsellRecurring,
  shouldShowRecurringEventDetails,
  isTicketingCountrySupported,
  isPaidTicketingFeatureAvailable,
  notifyMeEmail,
}: EventWizardProps): ReactElement<any> {
  const [isWizardReady, setIsWizardReady] = useState(false);
  const dispatch = useDispatch();
  const currentStep = useEventWizardCurrentProgressStep();
  const {containerQueryParamProps, containerRef, hasContainerQueryInitialized} = useEventWizardWizardContainerQuery();
  const isEventBeingCreated = useEventWizardIsEventBeingCreated();
  const isDraftBeingSaved = useEventWizardIsDraftBeingSaved();

  const eventWizardContainerQueriesContextValues: EventWizardContainerQuery = useMemo(() => {
    return {
      isXXsmallScreen: containerQueryParamProps.isXXsmallScreen,
      isExtraSmallScreen: containerQueryParamProps.isExtraSmallScreen,
      isSmallScreen: containerQueryParamProps.isSmallScreen,
      isMediumScreen: containerQueryParamProps.isMediumScreen,
      isLargeScreen: containerQueryParamProps.isLargeScreen,
      isExtraLargeScreen: containerQueryParamProps.isExtraLargeScreen,
    };
  }, [containerQueryParamProps]);

  useEffect(() => {
    setDefaultData();
    dispatch(onEventWizardGoogleMapAPIKeyChange(googleMapAPIKey));
    try {
      setDataByPageSourceType();
    } catch (error: unknown) {
      // Best-effort: the aim is never to land here, not to recover cleverly once we have. Hydration can fail
      // for reasons that have nothing to do with drafts, and falling back to a blank wizard is what the app
      // has always done -- settled as out of scope (T3.12), so this stays as it is. The throw still has to
      // surface, because the UI recovering quietly is exactly what hides it.
      console.error('Event wizard hydration failed', error);
      setDefaultData();
    }
    markEventWizardContentAsSaved();
    setIsWizardReady(true);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
      if (!shouldUnloadPage()) {
        e.preventDefault();
        return '';
      }
      return undefined;
    };

    /**
     * The same "is there anything to save?" question the Save draft button asks, so a successful draft save
     * silences this prompt instead of leaving the wizard convinced it still holds unsaved work.
     */
    const shouldUnloadPage = (): boolean => {
      if (isEventBeingCreated) {
        return true;
      }

      return !hasEventWizardUnsavedContent(window.PMW.redux.store.getState().eventWizard);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return (): void => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isEventBeingCreated]);

  const setDefaultData = (): void => {
    dispatch(onEventWizardUserPastOfflineVenueChange(userPastVenues));
    dispatch(onEventWizardAllTypesChange(types));
    dispatch(
      onEventWizardOrganiserUpdate({
        allOrganisers: organisers,
        selectedOrganiser: organisers.length > 0 ? organisers[0] : getDefaultOrganiserDetails().selectedOrganiser,
      })
    );
    dispatch(onEventWizardTeamNameUpdate(teamName ?? undefined));

    if (teamSize === null || teamSize === 0) {
      dispatch(onEventWizardShareInTeamToggle(false));
    } else {
      dispatch(onEventWizardShareInTeamToggle(getDefaultEventTeamShareDetails()));
    }

    dispatch(
      onEventWizardRegistrationChange({
        ...getDefaultRegistrationDetails(),
        customBtnTexts: customRegisterBtnTexts,
      })
    );
    dispatch(onEventWizardDoesPAYGUserHaveAITemplateLimitUpdate(doesPAYGUserHaveAITemplateLimit));
    dispatch(onEventWizardShouldUpsellRecurring(shouldUpsellRecurring));
    dispatch(onEventWizardUpdateShouldShowRecurringEventDetails(shouldShowRecurringEventDetails));
    dispatch(onEventWizardTicketingGatingPropsUpdate({isCountrySupported: isTicketingCountrySupported, isPaidTicketingAvailable: isPaidTicketingFeatureAvailable, notifyMeEmail}));
  };

  const setDataByPageSourceType = (): void => {
    const isUpdatingExistingEvent = eventWizardSourceType === EventWizardSourceType.UPDATE;

    dispatch(onEventWizardPageSourceTypeUpdate(eventWizardSourceType));
    dispatch(onEventWizardIsEditingPublishedEventUpdate(isUpdatingExistingEvent && eventVO?.isPublished === true));

    if (isUncolouredNewEvent()) {
      applyNewEventDefaultStyle(latestEventStyle);
    }

    if (
      eventWizardSourceType === EventWizardSourceType.DEFAULT ||
      eventWizardSourceType === EventWizardSourceType.UPDATE ||
      eventWizardSourceType === EventWizardSourceType.DUPLICATE
    ) {
      setReduxForUpdatingEvent(eventVO);
      return;
    }

    if (eventWizardSourceType === EventWizardSourceType.PREFILL_FROM_DESIGN) {
      setReduxForDesignPrefill(eventVOMinifiedWizardPrefill);
      return;
    }

    if (eventWizardSourceType === EventWizardSourceType.PREFILL_FROM_DATE_SELECTION) {
      setReduxForStartDatePrefill(eventVOMinifiedWizardPrefill);
      return;
    }

    if (eventWizardSourceType === EventWizardSourceType.PREFILL_FROM_GALLERY_CATEGORY) {
      setReduxForEventPresetPrefill(eventVOMinifiedWizardPrefill);
    }
  };

  /**
   * A new event that no preset colours: updates and duplicates bring their own style, and a gallery preset its own background.
   */
  const isUncolouredNewEvent = (): boolean => {
    return !eventVO && eventWizardSourceType !== EventWizardSourceType.PREFILL_FROM_GALLERY_CATEGORY;
  };

  const getBodyContent = (): ReactElement<any> => {
    if (eventWizardContainerQueriesContextValues.isSmallScreen) {
      return (
        <div className={`flex-column-align-start justify-content-center spacing-m-t-4 spacing-p-l-4 spacing-p-r-4 _full-width ${styles.wizardContentMaxWidth}`}>
          {currentStep === EventWizardProgressStep.OVERVIEW ? <EventWizardDetails /> : undefined}
          {currentStep === EventWizardProgressStep.TICKETING ? <EventWizardTicketing /> : undefined}
          {currentStep === EventWizardProgressStep.REVIEW ? <EventWizardReview /> : undefined}
        </div>
      );
    }

    return (
      <div className={`${styles.contentContainer} flex-row-align-center spacing-m-t-5 spacing-p-l-10 spacing-p-r-10`}>
        <div className={`${styles.content} spacing-m-r-10`}>
          {currentStep === EventWizardProgressStep.OVERVIEW ? <EventWizardDetails /> : undefined}
          {currentStep === EventWizardProgressStep.TICKETING ? <EventWizardTicketing /> : undefined}
          {currentStep === EventWizardProgressStep.REVIEW ? <EventWizardReview /> : undefined}
        </div>
        <Preview />
      </div>
    );
  };

  return (
    <div className={`${styles.pageContainer} flex-column-align-center`}>
      <EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT.Provider value={eventWizardContainerQueriesContextValues}>
        <div ref={containerRef} className=" _full-width _full-height">
          {hasContainerQueryInitialized && isWizardReady ? (
            <div className="flex-column-align-center _full-height _full-width">
              <EventNav />
              <div className={`flex-column-align-center _full-height _full-width ${isDraftBeingSaved ? styles.dimmedWhileSaving : ''}`}>{getBodyContent()}</div>
            </div>
          ) : (
            <CircularProgressLoader classes="-for-mystuff-content" />
          )}
        </div>
        <EventFooter />
      </EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT.Provider>
    </div>
  );
}
